import { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
  Badge,
  CheckCircleIcon,
  ChevronDownIcon,
  ClearSelectedIcon,
  CloseBoldV2Icon,
  IconCTA,
  OverlayPopover,
  Search,
  TableList,
} from '@utilities';

import { REVIEW_STATUS } from '@api/surveys/utilities/constants';

import styles from './_index.module.scss';

const RATER_DISPLAY_NAMES = {
  gibberish: 'Gibberish',
  moderation: 'Moderation',
  profanity: 'Profanity',
  relevance: 'Relevance',
  speeder: 'Speeder',
  straightline: 'Straightlining',
  videomoderation: 'Video Moderation',
};

const RATER_EXPANDED_DETAIL = {
  gibberish: null, // use reason from rating
  profanity: null, // use reason from rating
  relevance: null, // displayed as part of AI summary
  speeder: 'User ran this survey at an unrealistic pace.',
  straightline: (rating) => {
    const match = rating.reason?.match(/(\d+\/\d+)/);
    return match
      ? `User had repeatable patterns for ${match[1]} grid questions`
      : 'User had repeatable patterns across grid questions.';
  },
};

const FILTER_OPTIONS = [
  {
    group: 'Results',
    id: 'excluded',
    label: 'Excluded',
  },
  {
    group: 'Flags',
    id: 'speeder',
    label: 'Speeders',
  },
  {
    group: 'Flags',
    id: 'gibberish',
    label: 'Gibberish',
  },
  {
    group: 'Flags',
    id: 'profanity',
    label: 'Profanity',
  },
  {
    group: 'Flags',
    id: 'straightline',
    label: 'Straightliners',
  },
  {
    group: 'Flags',
    id: 'relevance',
    label: 'Relevance',
  },
];

const QUESTION_PREVIEW_LIMIT = 3;

const ANSWER_ARRAY_FIELDS = [
  'question_answers',
  'questionAnswers',
  'response_data',
  'responseData',
  'response_answers',
  'responseAnswers',
  'answers',
  'responses',
  'questionnaire_answers',
  'questionnaireAnswers',
  'questionnaire',
];

const ANSWER_VALUE_FIELDS = [
  'answer',
  'answerText',
  'answer_text',
  'answers',
  'display',
  'label',
  'response',
  'response_other',
  'response_value',
  'responseOther',
  'responseText',
  'response_text',
  'responseValue',
  'selectedAnswer',
  'selectedAnswers',
  'selected_answer',
  'selected_answers',
  'text_answer',
  'value',
];

const OPTION_ID_FIELDS = ['option_id', 'optionId', 'selected_option_id', 'selectedOptionId'];

const OPTION_TEXT_FIELDS = ['label', 'name', 'text', 'title', 'value'];

const QUESTION_ID_FIELDS = [
  'external_question_id',
  'externalQuestionId',
  'id',
  'question',
  'question_id',
  'questionId',
  'question_ref',
  'questionRef',
  'question_source_id',
  'questionSourceId',
  'source_id',
  'sourceId',
];

const QUESTION_TEXT_FIELDS = [
  'question_title',
  'questionTitle',
  'question_text',
  'questionText',
  'title',
  'text',
  'label',
  'name',
];

const formatDate = (isoString) => {
  if (!isoString) return '—';
  return isoString.split('T')[0];
};

const getFirstValue = (source, fields) => {
  if (!source || typeof source !== 'object') return undefined;

  for (const field of fields) {
    if (source[field] !== undefined && source[field] !== null && source[field] !== '') {
      return source[field];
    }
  }

  return undefined;
};

const getQuestionKey = (source) => {
  const value = getFirstValue(source, QUESTION_ID_FIELDS);
  if (value && typeof value === 'object') return getQuestionKey(value);
  return value === undefined ? undefined : String(value);
};

const getQuestionLookup = (surveyQuestions) => {
  const lookup = new Map();

  const addQuestion = (question, parentOptions) => {
    const hasQuestionOptions = Array.isArray(question?.options)
      ? question.options.length > 0
      : !!question?.options;
    const lookupQuestion =
      parentOptions && !hasQuestionOptions ? { ...question, options: parentOptions } : question;

    QUESTION_ID_FIELDS.forEach((field) => {
      if (lookupQuestion?.[field] !== undefined && lookupQuestion?.[field] !== null) {
        lookup.set(String(lookupQuestion[field]), lookupQuestion);
      }
    });

    question?.subQuestions?.forEach((subQuestion) => {
      addQuestion(subQuestion, question.options);
    });
    question?.sub_questions?.forEach((subQuestion) => {
      addQuestion(subQuestion, question.options);
    });
  };

  (surveyQuestions || []).forEach((question) => {
    addQuestion(question);
  });

  return lookup;
};

const getQuestionText = ({ answer, index, questionLookup }) => {
  const directQuestion =
    answer?.question && typeof answer.question === 'object' ? answer.question : undefined;
  const questionKey = getQuestionKey(answer);
  const matchedQuestion = questionKey ? questionLookup.get(questionKey) : undefined;
  const questionText =
    getFirstValue(answer, QUESTION_TEXT_FIELDS) ||
    getFirstValue(directQuestion, QUESTION_TEXT_FIELDS) ||
    getFirstValue(matchedQuestion, QUESTION_TEXT_FIELDS);

  return questionText || `Question ${index + 1}`;
};

const normalizeAnswerLines = (value) => {
  if (value === undefined || value === null || value === '') return [];

  if (Array.isArray(value)) {
    return value.flatMap(normalizeAnswerLines).filter(Boolean);
  }

  if (typeof value === 'object') {
    const directValue = getFirstValue(
      value,
      ANSWER_VALUE_FIELDS.filter((field) => field !== 'value')
    );
    if (directValue !== undefined) return normalizeAnswerLines(directValue);

    if (value.value !== undefined) return normalizeAnswerLines(value.value);

    return Object.entries(value)
      .filter(([, entryValue]) => ['boolean', 'number', 'string'].includes(typeof entryValue))
      .map(([entryKey, entryValue]) => `${entryKey}: ${entryValue}`);
  }

  return [String(value)];
};

const getOptionAnswerValue = ({ answer, matchedQuestion }) => {
  const optionId = getFirstValue(answer, OPTION_ID_FIELDS);
  if (optionId === undefined) return [];

  const matchedOption = (matchedQuestion?.options || matchedQuestion?.answers || []).find(
    (option) =>
      String(getFirstValue(option, ['id', ...OPTION_ID_FIELDS, 'value'])) === String(optionId)
  );
  const optionText = getFirstValue(matchedOption, OPTION_TEXT_FIELDS);

  return normalizeAnswerLines(optionText || `Option ${optionId}`);
};

const getAnswerValue = ({ answer, questionLookup }) => {
  const value = getFirstValue(answer, ANSWER_VALUE_FIELDS);
  const answerLines = normalizeAnswerLines(value);

  if (answerLines.length > 0) return answerLines;

  const questionKey = getQuestionKey(answer);
  const matchedQuestion = questionKey ? questionLookup.get(questionKey) : undefined;
  return getOptionAnswerValue({ answer, matchedQuestion });
};

const extractQuestionAnswers = ({ response, surveyQuestions }) => {
  const questionLookup = getQuestionLookup(surveyQuestions);
  const responseSources = [response, response?.response].filter(Boolean);

  for (const responseSource of responseSources) {
    if (Array.isArray(responseSource?.questions)) {
      return responseSource.questions
        .map((question, index) => ({
          answers: getAnswerValue({ answer: question, questionLookup }),
          id: getQuestionKey(question) || `question-${index}`,
          question: getQuestionText({ answer: question, index, questionLookup }),
        }))
        .filter(({ answers }) => answers.length > 0);
    }

    for (const field of ANSWER_ARRAY_FIELDS) {
      const value = responseSource?.[field];

      if (Array.isArray(value)) {
        return value
          .map((answer, index) => ({
            answers: getAnswerValue({ answer, questionLookup }),
            id: getQuestionKey(answer) || `${field}-${index}`,
            question: getQuestionText({ answer, index, questionLookup }),
          }))
          .filter(({ answers }) => answers.length > 0);
      }

      if (value && typeof value === 'object') {
        return Object.entries(value)
          .map(([questionKey, answerValue], index) => {
            const matchedQuestion = questionLookup.get(String(questionKey));
            return {
              answers: normalizeAnswerLines(answerValue),
              id: questionKey,
              question:
                getFirstValue(matchedQuestion, QUESTION_TEXT_FIELDS) || `Question ${index + 1}`,
            };
          })
          .filter(({ answers }) => answers.length > 0);
      }
    }
  }

  return [];
};

const FlagBadges = ({ ratings }) => {
  const flagged = ratings?.filter((r) => r.flagged) ?? [];
  if (!flagged.length) return null;
  return (
    <span className={styles['flag-badges']}>
      {flagged.map((r) => (
        <Badge
          className={styles['flag-badge']}
          key={r.rater_name}
          label={RATER_DISPLAY_NAMES[r.rater_name] ?? r.rater_name}
          variant="default"
        />
      ))}
    </span>
  );
};

FlagBadges.propTypes = {
  ratings: PropTypes.arrayOf(PropTypes.object),
};

const ExpandedRowContent = ({ onOverride, response, surveyQuestions }) => {
  const [isQuestionnaireExpanded, setIsQuestionnaireExpanded] = useState(false);
  const flaggedRatings = response.ratings?.filter((r) => r.flagged) ?? [];
  const questionAnswers = useMemo(
    () => extractQuestionAnswers({ response, surveyQuestions }),
    [response, surveyQuestions]
  );
  const visibleQuestionAnswers = isQuestionnaireExpanded
    ? questionAnswers
    : questionAnswers.slice(0, QUESTION_PREVIEW_LIMIT);
  const hasMoreQuestions = questionAnswers.length > QUESTION_PREVIEW_LIMIT;
  const isExcluded = response.review_status === REVIEW_STATUS.QUARANTINED;

  const handleOverrideClick = () => {
    onOverride({
      quarantine: !isExcluded,
      reason: isExcluded ? 'Manually reinstated' : 'Manually removed',
      responseId: response.id,
    });
  };

  return (
    <div className={styles['expanded-content']}>
      <div className={styles['expanded-flags']}>
        <h4 className={styles['expanded-section-heading']}>Flags</h4>
        {flaggedRatings.length === 0 ? (
          <p className={styles['expanded-empty']}>No issues found.</p>
        ) : (
          flaggedRatings.map((rating) => {
            const staticDetail = RATER_EXPANDED_DETAIL[rating.rater_name];
            const detail =
              typeof staticDetail === 'function'
                ? staticDetail(rating)
                : (staticDetail ?? rating.reason);
            return (
              <div className={styles['expanded-flag']} key={rating.rater_name}>
                <Badge
                  className={styles['expanded-flag-badge']}
                  label={RATER_DISPLAY_NAMES[rating.rater_name] ?? rating.rater_name}
                  variant="default"
                />
                {detail && <p className={styles['expanded-flag-detail']}>{detail}</p>}
              </div>
            );
          })
        )}
      </div>

      {questionAnswers.length > 0 && (
        <div className={styles['expanded-questionnaire']}>
          <h4 className={styles['expanded-section-heading']}>Survey Answers</h4>
          {visibleQuestionAnswers.map(({ answers, id, question }, index) => (
            <div className={styles['expanded-question-answer']} key={id}>
              <p className={styles['expanded-question']}>
                Q{index + 1}. {question}
              </p>
              <div className={styles['expanded-answers']}>
                {answers.map((answer, answerIndex) => (
                  <p key={`${id}-answer-${answerIndex}`}>{answer}</p>
                ))}
              </div>
            </div>
          ))}
          {hasMoreQuestions && (
            <button
              className={styles['expanded-see-more']}
              onClick={() => setIsQuestionnaireExpanded((current) => !current)}
              type="button"
            >
              {isQuestionnaireExpanded ? 'See Less' : 'See All Answers'}
            </button>
          )}
        </div>
      )}

      <div className={styles['expanded-result-status']}>
        <span className={styles['expanded-result-icon']}>
          <CloseBoldV2Icon />
        </span>
        <span>
          This response is <strong>{isExcluded ? 'excluded' : 'included'}</strong> from your
          results.
        </span>
        <button
          className={styles['expanded-result-action']}
          onClick={handleOverrideClick}
          type="button"
        >
          {isExcluded ? 'Include' : 'Exclude'}
        </button>
      </div>
    </div>
  );
};

ExpandedRowContent.propTypes = {
  onOverride: PropTypes.func.isRequired,
  response: PropTypes.object.isRequired,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
};

const FilterDropdown = ({ activeFilters, onFilterChange }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const triggerRef = useRef(null);

  const groups = FILTER_OPTIONS.reduce((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {});

  const activeOptions = FILTER_OPTIONS.filter((opt) => activeFilters[opt.id]);

  const handleClearAll = (event) => {
    event.stopPropagation();
    onFilterChange({});
  };

  const handleRemoveFilter = (event, id) => {
    event.stopPropagation();
    const nextFilters = { ...activeFilters };
    delete nextFilters[id];
    onFilterChange(nextFilters);
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      setIsPopoverOpen(true);
    }

    if (event.key === 'Escape') {
      setIsPopoverOpen(false);
    }
  };

  return (
    <>
      <div
        aria-expanded={isPopoverOpen}
        aria-haspopup="menu"
        aria-label="Filter By"
        className={classNames(styles['filter-trigger'], {
          [styles['has-active-filters']]: activeOptions.length > 0,
          [styles['is-active']]: isPopoverOpen,
        })}
        onClick={() => setIsPopoverOpen(true)}
        onKeyDown={handleTriggerKeyDown}
        ref={triggerRef}
        role="button"
        tabIndex={0}
      >
        <span className={styles['filter-trigger-content']}>
          {activeOptions.length > 0 ? (
            activeOptions.map((opt) => (
              <span className={styles['filter-chip']} key={opt.id}>
                <span className={styles['filter-chip-label']}>{opt.label}</span>
                <button
                  aria-label={`Remove ${opt.label} filter`}
                  className={styles['filter-chip-remove']}
                  onClick={(event) => handleRemoveFilter(event, opt.id)}
                  type="button"
                >
                  <CloseBoldV2Icon />
                </button>
              </span>
            ))
          ) : (
            <span className={styles['filter-placeholder']}>Filter By</span>
          )}
        </span>
        {activeOptions.length > 0 && (
          <>
            <span aria-hidden="true" className={styles['filter-trigger-divider']} />
            <button
              aria-label="Clear filters"
              className={styles['filter-trigger-icon-button']}
              onClick={handleClearAll}
              type="button"
            >
              <CloseBoldV2Icon />
            </button>
            <span aria-hidden="true" className={styles['filter-trigger-divider']} />
          </>
        )}
        <span className={styles['filter-trigger-chevron']}>
          <ChevronDownIcon />
        </span>
      </div>
      <OverlayPopover
        hasArrow={false}
        isActive={isPopoverOpen}
        onHide={() => setIsPopoverOpen(false)}
        placement="bottom-start"
        target={triggerRef.current}
      >
        <div className={styles['filter-popover']}>
          {Object.entries(groups).map(([group, options]) => (
            <div className={styles['filter-group']} key={group}>
              <p className={styles['filter-group-label']}>{group}</p>
              {options.map((opt) => (
                <label className={styles['filter-option']} key={opt.id}>
                  <input
                    checked={!!activeFilters[opt.id]}
                    onChange={(e) =>
                      onFilterChange({ ...activeFilters, [opt.id]: e.target.checked })
                    }
                    type="checkbox"
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          ))}
          {activeOptions.length > 0 && (
            <button className={styles['filter-clear']} onClick={handleClearAll} type="button">
              Clear all
            </button>
          )}
        </div>
      </OverlayPopover>
    </>
  );
};

FilterDropdown.propTypes = {
  activeFilters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

const ResponseIdCell = ({ value }) => <span className={styles['response-id']}>{value}</span>;
ResponseIdCell.propTypes = { value: PropTypes.number };

const DateCell = ({ value }) => formatDate(value);
DateCell.propTypes = { value: PropTypes.string };

const ExpanderCell = ({ row }) => {
  const handleToggle = (event) => {
    event.stopPropagation();
    row.getToggleRowExpandedProps().onClick();
  };

  return (
    <span
      aria-label={row.isExpanded ? 'Collapse response details' : 'Expand response details'}
      className={classNames(styles['expand-toggle'], {
        [styles['is-expanded']]: row.isExpanded,
      })}
      onClick={handleToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleToggle(event);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <ChevronDownIcon />
    </span>
  );
};
ExpanderCell.propTypes = { row: PropTypes.object.isRequired };

const StatusCell = ({ value }) => {
  if (value === REVIEW_STATUS.QUARANTINED) {
    return (
      <span className={styles['status-excluded-icon']} title="Excluded">
        <CloseBoldV2Icon />
      </span>
    );
  }
  if (value === REVIEW_STATUS.PENDING) return <Badge label="Pending" variant="default" />;
  return null;
};
StatusCell.propTypes = { value: PropTypes.string };

const FlagsCell = ({ value }) => <FlagBadges ratings={value} />;
FlagsCell.propTypes = { value: PropTypes.arrayOf(PropTypes.object) };

const AiSummaryCell = ({ value }) => (
  <span className={styles['ai-summary']} title={value || 'No issues found.'}>
    {value || 'No issues found.'}
  </span>
);
AiSummaryCell.propTypes = { value: PropTypes.string };

const BulkActionsToolbar = ({ onClear, onExclude, onInclude, selectedRows }) => {
  if (selectedRows.length === 0) return null;

  return (
    <div className={styles['bulk-actions-toolbar-wrapper']}>
      <div
        aria-label="Selected response actions"
        className={styles['bulk-actions-toolbar']}
        role="toolbar"
      >
        <div className={styles['bulk-actions-toolbar-group']}>
          <IconCTA
            icon={<ClearSelectedIcon />}
            onClick={onClear}
            text={`Clear selected (${selectedRows.length})`}
          />
        </div>
        <div className={styles['bulk-actions-toolbar-divider']} />
        <div className={styles['bulk-actions-toolbar-group']}>
          <IconCTA icon={<CloseBoldV2Icon />} onClick={onExclude} text="Exclude" />
          <IconCTA icon={<CheckCircleIcon />} onClick={onInclude} text="Include" />
        </div>
      </div>
    </div>
  );
};

BulkActionsToolbar.propTypes = {
  onClear: PropTypes.func.isRequired,
  onExclude: PropTypes.func.isRequired,
  onInclude: PropTypes.func.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const COLUMNS = [
  {
    align: 'center',
    Cell: ExpanderCell,
    disableSortBy: true,
    Header: () => null,
    id: 'expander',
    maxWidth: 35,
    minWidth: 35,
    width: 35,
  },
  {
    accessor: 'id',
    align: 'left',
    Cell: ResponseIdCell,
    Header: 'Response ID',
    minWidth: 80,
    width: 100,
  },
  {
    accessor: 'created',
    align: 'left',
    Cell: DateCell,
    Header: 'Response Date',
    minWidth: 85,
    width: 100,
  },
  {
    accessor: 'review_status',
    align: 'left',
    Cell: StatusCell,
    disableSortBy: true,
    Header: 'Status',
    minWidth: 65,
    width: 80,
  },
  {
    accessor: 'ratings',
    align: 'left',
    Cell: FlagsCell,
    disableSortBy: true,
    Header: 'Flags',
    minWidth: 120,
    width: 220,
  },
  {
    accessor: 'ai_summary',
    align: 'left',
    Cell: AiSummaryCell,
    disableSortBy: true,
    Header: 'Review By Numerator AI',
    minWidth: 320,
  },
];

const ResponseTable = ({
  activeFilters,
  currentPage,
  isLoading,
  onFilterChange,
  onOverride,
  onPageChange,
  onSearchChange,
  onSortChange,
  pageCount,
  responses,
  searchId,
  surveyQuestions,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);

  const columns = useMemo(() => COLUMNS, []);
  const selectedResponseIds = selectedRows.map((response) => response.id);

  const handleBulkOverride = async ({ quarantine }) => {
    if (selectedResponseIds.length === 0) return;

    const wasSuccessful = await onOverride({
      quarantine,
      reason: quarantine ? 'Manually removed' : 'Manually reinstated',
      responseIds: selectedResponseIds,
    });
    if (wasSuccessful) setSelectedRows([]);
  };

  return (
    <div className={styles['response-table']}>
      <div className={styles['table-controls']}>
        <Search onChange={onSearchChange} placeholder="Search by ID" value={searchId} />
        <FilterDropdown activeFilters={activeFilters} onFilterChange={onFilterChange} />
      </div>
      <TableList
        columns={columns}
        currentPage={currentPage}
        data={responses}
        initialSortBy={[{ desc: false, id: 'id' }]}
        isLoading={isLoading}
        pageCount={pageCount}
        renderEmptyMessage={() => <p className={styles['empty-message']}>No responses found.</p>}
        renderRowExpanded={({ row }) => (
          <ExpandedRowContent
            onOverride={onOverride}
            response={row.original}
            surveyQuestions={surveyQuestions}
          />
        )}
        selectedRows={selectedRows}
        setPage={onPageChange}
        setSelectedRows={setSelectedRows}
        setSortBy={onSortChange}
        size={10}
      />
      <BulkActionsToolbar
        onClear={() => setSelectedRows([])}
        onExclude={() => handleBulkOverride({ quarantine: true })}
        onInclude={() => handleBulkOverride({ quarantine: false })}
        selectedRows={selectedRows}
      />
    </div>
  );
};

ResponseTable.propTypes = {
  activeFilters: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  onFilterChange: PropTypes.func.isRequired,
  onOverride: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  responses: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchId: PropTypes.string.isRequired,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
};

export default ResponseTable;
